import "~/Core/Assets/css/style.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import React, { Suspense, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import Loader from "~/Core/Components/Extra/Loader";
import TypesafeI18n from "~/i18n/i18n-react";
import { loadLocaleAsync } from "~/i18n/i18n-util.async";
import AccountLayout from "~/Pages/Shared/Layout/Account";
import { useLanguageStore } from "~/Store/index";

const ViewError = React.lazy(() => import("./Pages/Shared/Error"));
const NotAllowed = React.lazy(() => import("./Pages/Shared/NotAllowed"));
const Portal = React.lazy(() => import("./Pages/Portal"));
const Dashboard = React.lazy(() => import("./Pages/Dashboard"));
const SetPasswordFrom = React.lazy(() => import("./pages/portal/account/partial/setUpPasswordFrom"));

const SUPPORT_EMAIL = import.meta.env.VITE_SUPPORT_EMAIL as string;
function SupportButton() {
  return (
    <a
      aria-label={"Help"}
      className={[
        "group fixed bottom-6 z-[100] inline-flex items-center gap-2.5 overflow-hidden rounded-[22px] border border-white/35 bg-gradient-to-br from-[#1b8a78] via-[#157567] to-[#0f5b53] px-4 py-3 text-white shadow-[0_14px_30px_rgba(7,46,42,0.18)] backdrop-blur-xl transition-all duration-500 ease-out hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(7,46,42,0.22)] focus:outline-none focus:ring-2 focus:ring-[#1b8a78] focus:ring-offset-2",
        "right-6",
      ].join(" ")}
      href={`mailto:${SUPPORT_EMAIL}`}>
      <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.18),transparent_58%)] opacity-70" />
      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/14 ring-1 ring-white/15 transition-all duration-500 ease-out group-hover:bg-white/18">
        <svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24">
          <path
            d="M4 6.75A1.75 1.75 0 0 1 5.75 5h12.5A1.75 1.75 0 0 1 20 6.75v10.5A1.75 1.75 0 0 1 18.25 19H5.75A1.75 1.75 0 0 1 4 17.25V6.75Zm1.9-.25L12 11.16l6.1-4.66H5.9Zm12.6 1.88-5.9 4.51a1 1 0 0 1-1.2 0L5.5 8.38v8.87c0 .14.11.25.25.25h12.5a.25.25 0 0 0 .25-.25V8.38Z"
            fill="currentColor"
          />
        </svg>
      </span>
      <span className="relative flex flex-col leading-none">
        <span className="text-[10px] font-medium uppercase tracking-[0.22em] text-white/65">{"Support"}</span>
        <span className="mt-1 text-sm font-semibold">{"Help Center"}</span>
      </span>
    </a>
  );
}

function AnimatedRoute() {
  const location = useLocation();
  const { pathname } = useLocation();
  const [wasLoaded, setWasLoaded] = useState(false);
  const { Language } = useLanguageStore();

  useEffect(() => {
    document.body.dir = Language === "ar" ? "rtl" : "ltr";
    loadLocaleAsync(Language).then(() => setWasLoaded(true));
  }, [Language]);

  useEffect(() => {
    window.scrollTo({
      behavior: "instant",
      left: 0,
      top: 0,
    });
  }, [pathname]);

  if (!wasLoaded) return null;

  return (
    <AnimatePresence initial={false} key="mainBody" mode="wait">
      <TypesafeI18n locale={Language}>
        <div className="contents">
          <Routes key={location.pathname} location={location}>
            <Route element={<Portal />} path="Portal/*" />
            <Route element={<Dashboard />} path="Dashboard/*" />
            <Route element={<AccountLayout />} path="supplier/confirm">
              <Route element={<SetPasswordFrom />} index />
            </Route>
            <Route element={<ViewError />} path="/error/*" />
            <Route element={<NotAllowed />} path="/notAllowed/*" />
            <Route element={<Navigate to="/Portal/" />} path="/" />
            <Route element={<Navigate to="/" />} path="*" />
          </Routes>
          <SupportButton />
        </div>
      </TypesafeI18n>
    </AnimatePresence>
  );
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      retryDelay: 1000 * 5 * 1,
    },
  },
});
function App() {
  return (
    <Suspense fallback={<Loader />}>
      <BrowserRouter
        future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true,
        }}>
        <AnimatedRoute />
      </BrowserRouter>
    </Suspense>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root") as Element);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <ToastContainer autoClose={5000} closeOnClick pauseOnHover position="top-center" rtl={false} />
    </QueryClientProvider>
  </React.StrictMode>,
);
